import type { ErrorInfo } from 'react';
import {
  createBrowserLogger,
  detectBrowserGlobals,
  installGlobalErrorCapture,
  type BrowserGlobals,
  type BrowserLogger,
} from '@speles7172/log-client/browser';
import { ApiError } from './api';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

/** The mobile app's `source` on every record; the ingest endpoint refuses any it doesn't list. */
export const BROWSER_LOG_SOURCE = 'merkos-mgmt-mobile';

/** The ingest endpoint (backend `handlers/admin-config/log-ingest.ts`), under the admin router's catch-all. */
export const BROWSER_LOG_ENDPOINT = `${API_BASE}/admin/logs/ingest`;

let logger: BrowserLogger | null = null;
let removeCapture: (() => void) | null = null;

/**
 * Ship the mobile app's errors to the log monitor (Admin → Monitor → Logs,
 * source `merkos-mgmt-mobile`, origin "browser"): uncaught errors, unhandled
 * rejections, what the error boundary catches ({@link reportRenderError}) and
 * failures the app handles with a toast ({@link reportHandledError}).
 *
 * Same transport as the web app's logger: `fetch` with the session cookie and
 * `keepalive`, never `sendBeacon` (which can't send the cookie's credentials
 * mode). Records logged while signed out are refused and dropped; never retried.
 * Idempotent: a second call returns the same logger.
 */
export function startBrowserLogging(globals: BrowserGlobals = detectBrowserGlobals()): BrowserLogger {
  if (logger) return logger;
  const withoutBeacon = { ...globals, sendBeacon: undefined };
  logger = createBrowserLogger({
    endpoint: BROWSER_LOG_ENDPOINT,
    source: BROWSER_LOG_SOURCE,
    credentials: 'include',
    globals: withoutBeacon,
  });
  removeCapture = installGlobalErrorCapture(logger, withoutBeacon);
  return logger;
}

/** Report an error the React error boundary caught (React swallows these in production). */
export function reportRenderError(error: unknown, info?: ErrorInfo): void {
  logger?.log({
    level: 'error',
    event: 'browser.render-error',
    error,
    fields: info?.componentStack ? { componentStack: info.componentStack } : {},
  });
}

/**
 * Report a failure the app caught and showed the user (a toast), which would
 * otherwise leave no trace. `event` is a stable name (e.g. `mobile.timer.stop_failed`);
 * an API failure adds its status and the server's message. Use `info` for an
 * expected refusal the user can resolve (e.g. a time rule), so it doesn't read as a fault.
 */
export function reportHandledError(
  event: string,
  error: unknown,
  fields: Record<string, unknown> = {},
  level: 'info' | 'warn' | 'error' = 'warn',
): void {
  logger?.log({
    level,
    event,
    error,
    fields: error instanceof ApiError ? { status: error.status, ...fields } : fields,
  });
}

/** Tests: drop the logger and its listeners. */
export async function stopBrowserLogging(): Promise<void> {
  removeCapture?.();
  removeCapture = null;
  const current = logger;
  logger = null;
  await current?.close();
}
